import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { AlertTriangle, Clock, DollarSign, KeyRound, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REASON_OPTIONS = [
  { value: 'customer_request', label: 'Customer request' },
  { value: 'venue_issue', label: 'Venue issue' },
  { value: 'staff_shortage', label: 'Staff shortage' },
  { value: 'weather', label: 'Weather (outdoor)' },
  { value: 'no_show', label: 'No-show' },
  { value: 'other', label: 'Other (specify below)' },
];

export const CancelBookingModal = ({ booking, open, onClose, onSuccess }) => {
  const [reason, setReason] = useState('customer_request');
  const [comment, setComment] = useState('');
  const [pin, setPin] = useState('');
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || !booking?.booking_id) return;
    setReason('customer_request');
    setComment('');
    setPin('');
    setResult(null);
    setPreview(null);
    fetchPreview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking?.booking_id]);

  const fetchPreview = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/bookings/${booking.booking_id}/cancel-preview`, { withCredentials: true });
      setPreview(res.data);
    } catch (e) {
      toast.error('Failed to load refund preview');
    }
  };

  const refundAmount = (() => {
    if (!preview) return 0;
    const within = preview.hours_to_party <= 24 || reason === 'no_show';
    return within ? preview.preview_refund_within_24h_or_noshow : preview.preview_refund_over_24h;
  })();

  const refundPct = (() => {
    if (!preview) return 0;
    const within = preview.hours_to_party <= 24 || reason === 'no_show';
    return within ? 50 : 100;
  })();

  const submit = async () => {
    if (reason === 'other' && !comment.trim()) {
      toast.error('Please add a comment for "Other" reason');
      return;
    }
    if (!/^\d{4}$/.test(pin)) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/bookings/${booking.booking_id}/cancel`,
        { reason, comment, pin },
        { withCredentials: true }
      );
      setResult(res.data);
      toast.success(`Cancelled. Refund: $${res.data.refund_amount.toFixed(2)}`);
      onSuccess?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    } finally {
      setSubmitting(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="cancel-booking-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Fredoka' }} className="flex items-center gap-2 text-[#FF3B6B]">
            <AlertTriangle className="w-5 h-5" /> Cancel Booking
          </DialogTitle>
        </DialogHeader>

        {result ? (
          // Success view
          <div className="space-y-4" data-testid="cancel-success">
            <div className="p-4 rounded-xl bg-[#06D6A0]/10 border border-[#06D6A0]/30 flex gap-3">
              <CheckCircle2 className="w-6 h-6 text-[#06D6A0] flex-shrink-0" />
              <div>
                <p className="font-semibold text-[#06D6A0]">Booking Cancelled</p>
                <p className="text-sm text-[#1E1E24]">Refund processed: <strong>${result.refund_amount.toFixed(2)}</strong> ({result.refund_percentage}%)</p>
                <p className="text-xs text-[#6B6B7A] mt-1">Stripe refunds typically arrive in 5-10 business days.</p>
              </div>
            </div>
            {result.cancellation_pending && (
              <div className="p-3 rounded-xl bg-yellow-50 border border-yellow-200 text-xs text-yellow-800" data-testid="cancel-pending-warning">
                ⚠ Some refunds couldn't be processed automatically. Status set to <strong>cancellation_pending</strong> — please review in CRM.
              </div>
            )}
            {result.refund_results?.some(r => r.payment_method === 'paypal') && (
              <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 text-xs text-blue-800">
                ℹ PayPal refunds require manual processing via your PayPal dashboard.
              </div>
            )}
            <Button onClick={onClose} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="cancel-close-btn">
              Close
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Step 1: Reason */}
            <div>
              <Label className="font-semibold">Reason for cancellation *</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="cancel-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REASON_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="font-semibold">Additional comment {reason === 'other' ? '*' : '(optional)'}</Label>
              <Textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                rows={2}
                placeholder={reason === 'other' ? 'Please describe the reason...' : 'Any extra context...'}
                className="mt-1 rounded-xl border-[#E8E5DF]"
                data-testid="cancel-comment"
              />
            </div>

            {/* Step 2: Refund preview */}
            {preview && (
              <div className="p-4 rounded-xl bg-[#FFFDF8] border border-[#E8E5DF] space-y-1.5 text-sm" data-testid="refund-preview">
                <div className="flex items-center gap-2 font-semibold text-[#1E1E24]">
                  <Clock className="w-4 h-4 text-[#6B6B7A]" /> Party Schedule
                </div>
                <p className="text-xs text-[#6B6B7A]">
                  {preview.party_date} at {preview.party_time} · <strong>{preview.hours_to_party.toFixed(1)}h</strong> from now
                </p>
                <div className="flex items-center gap-2 font-semibold text-[#1E1E24] mt-2">
                  <DollarSign className="w-4 h-4 text-[#6B6B7A]" /> Refund Breakdown
                </div>
                <div className="flex justify-between text-xs">
                  <span>Total paid:</span>
                  <span className="font-mono">${preview.total_paid.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span>Refund rate:</span>
                  <span className={`font-bold ${refundPct === 100 ? 'text-[#06D6A0]' : 'text-[#FFD166]'}`}>
                    {refundPct}% {refundPct === 100 ? '(>24h before party)' : reason === 'no_show' ? '(no-show)' : '(≤24h before party)'}
                  </span>
                </div>
                <div className="flex justify-between pt-1 border-t border-[#E8E5DF]">
                  <span className="font-semibold text-[#06D6A0]">Refund amount:</span>
                  <span className="font-bold text-[#06D6A0] font-mono" data-testid="refund-amount-display">${refundAmount.toFixed(2)}</span>
                </div>
                {refundPct === 50 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-[#FF3B6B]">Non-refundable:</span>
                    <span className="font-mono text-[#FF3B6B]">${(preview.total_paid - refundAmount).toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: PIN */}
            <div>
              <Label className="font-semibold flex items-center gap-1">
                <KeyRound className="w-4 h-4 text-[#6B6B7A]" /> Employee PIN *
              </Label>
              <Input
                type="password"
                inputMode="numeric"
                value={pin}
                onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="4-digit PIN"
                maxLength={4}
                className="mt-1 rounded-xl border-[#E8E5DF] font-mono tracking-widest"
                data-testid="cancel-pin-input"
              />
              <p className="text-xs text-[#6B6B7A] mt-1">3 wrong attempts locks you out for 5 minutes.</p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1 rounded-full border-[#E8E5DF]" data-testid="cancel-abort-btn">
                Keep Booking
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || pin.length !== 4}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full"
                data-testid="confirm-cancel-btn"
              >
                {submitting ? 'Processing...' : `Confirm & Refund $${refundAmount.toFixed(2)}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CancelBookingModal;
